import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Menu, Sun, Moon } from 'lucide-react';
import { useTheme } from '../App';
interface BurgerMenuProps {
  onGoToAdmin?: () => void;
  onGoToProjects?: () => void;
  onGoHome?: () => void;
  currentPage?: 'home' | 'projects' | 'admin';
}
export function BurgerMenu({
  onGoToAdmin,
  onGoToProjects,
  onGoHome,
  currentPage = 'home'
}: BurgerMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const {
    theme,
    toggleTheme
  } = useTheme();
  const isDark = theme === 'dark';
  const leftMenuItems = [{
    title: 'Work',
    href: '#work',
    page: 'home' as const
  }, {
    title: 'Projects',
    href: '#projects',
    page: 'projects' as const
  }];
  const rightMenuItems = [{
    title: 'About',
    href: '#about',
    page: 'home' as const
  }, {
    title: 'Contact',
    href: '#contact',
    page: 'home' as const
  }];
  const menuVariants = {
    closed: {
      x: '100%',
      transition: {
        duration: 0.8,
        ease: [0.76, 0, 0.24, 1]
      }
    },
    open: {
      x: 0,
      transition: {
        duration: 0.8,
        ease: [0.76, 0, 0.24, 1]
      }
    }
  };
  const itemVariants = {
    closed: {
      x: 80,
      opacity: 0
    },
    open: (i: number) => ({
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.76, 0, 0.24, 1],
        delay: 0.1 + i * 0.1
      }
    })
  };
  const handleAdminClick = () => {
    setIsOpen(false);
    onGoToAdmin?.();
  };
  const handleMenuClick = (href: string, page: 'home' | 'projects') => {
    setIsOpen(false);
    // Handle Projects navigation
    if (href === '#projects') {
      // If already on projects page, do nothing
      if (currentPage === 'projects') {
        return;
      }
      // Otherwise navigate to projects page
      onGoToProjects?.();
      return;
    }
    // Handle other sections - go home first if needed
    if (href !== '#' && (href === '#work' || href === '#about' || href === '#contact')) {
      // If we're not on home page, go home first
      if (currentPage !== 'home') {
        if (onGoHome) {
          onGoHome();
          // Wait for navigation, then scroll
          setTimeout(() => {
            const element = document.querySelector(href);
            if (element) {
              element.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }
          }, 100);
          return;
        }
      }
    }
    // Smooth scroll to section (if on home page)
    if (href === '#') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  };
  return <>
      <div className="fixed top-8 right-8 z-[70]" data-id="element-12">
        <button onClick={() => setIsOpen(!isOpen)} className="w-12 h-12 flex items-center justify-center hover:scale-110 transition-transform duration-300 group" aria-label="Menu" data-id="element-13">
          <div className="relative w-8 h-8 flex items-center justify-center" data-id="element-14">
            <Menu className={`absolute w-7 h-7 transition-all duration-300 ${isOpen ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'} ${isDark ? 'text-white' : 'text-black'}`} data-id="element-15" />
            <X className={`absolute w-7 h-7 transition-all duration-300 ${isOpen ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'} ${isDark ? 'text-white' : 'text-black'}`} data-id="element-16" />
          </div>
        </button>
      </div>

      <AnimatePresence data-id="element-17">
        {isOpen && <motion.div variants={menuVariants} initial="closed" animate="open" exit="closed" className={`fixed top-0 right-0 h-screen w-full z-[60] border-l ${isDark ? 'bg-neutral-950 border-white/10' : 'bg-neutral-50 border-black/10'}`} data-id="element-18">
            {/* Jav Logo - Top Left */}
            <motion.div variants={itemVariants} custom={0} className="absolute top-8 left-8" data-id="element-19">
              <button onClick={() => {
            setIsOpen(false);
            if (onGoHome) {
              onGoHome();
            } else {
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            }
          }} className={`text-2xl font-black tracking-tighter transition-opacity duration-300 hover:opacity-70 ${isDark ? 'text-white' : 'text-black'}`} data-id="element-20">
                Jav.
              </button>
            </motion.div>

            {/* Split Columns Layout */}
            <div className="h-full flex" data-id="element-21">
              {/* Left Column */}
              <div className="flex-1 flex flex-col justify-center items-center p-12 md:p-24" style={{
            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.02)'
          }} data-id="element-22">
                <div className="flex flex-col gap-8 w-full max-w-md" data-id="element-23">
                  {leftMenuItems.map((item, i) => {
                const isActive = item.page === 'projects' && currentPage === 'projects' || item.page === 'home' && item.href === '#work' && currentPage === 'home';
                return <motion.div key={i} custom={i + 1} variants={itemVariants} className="text-center" data-id="element-24">
                        <button onClick={() => handleMenuClick(item.href, item.page)} className={`text-5xl md:text-7xl font-black tracking-tighter transition-colors ${isActive ? isDark ? 'text-white' : 'text-black' : isDark ? 'text-white/40 hover:text-white/70' : 'text-black/40 hover:text-black/70'}`} data-id="element-25">
                          {item.title}
                        </button>
                      </motion.div>;
              })}
                </div>
              </div>

              {/* Vertical Divider */}
              <div className="w-px" style={{
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
          }} data-id="element-26" />

              {/* Right Column */}
              <div className="flex-1 flex flex-col justify-center items-center p-12 md:p-24" data-id="element-27">
                <div className="flex flex-col gap-8 w-full max-w-md" data-id="element-28">
                  {rightMenuItems.map((item, i) => {
                const isActive = currentPage === 'home' && false; // These sections don't have active state
                return <motion.div key={i} custom={i + 3} variants={itemVariants} className="text-center" data-id="element-29">
                        <button onClick={() => handleMenuClick(item.href, item.page)} className={`text-5xl md:text-7xl font-black tracking-tighter transition-colors ${isActive ? isDark ? 'text-white' : 'text-black' : isDark ? 'text-white/40 hover:text-white/70' : 'text-black/40 hover:text-black/70'}`} data-id="element-30">
                          {item.title}
                        </button>
                      </motion.div>;
              })}
                </div>
              </div>
            </div>

            {/* Admin Link - Bottom Left */}
            {onGoToAdmin && <motion.div variants={itemVariants} custom={5} className="absolute bottom-8 left-8" data-id="element-31">
                <button onClick={handleAdminClick} className={`text-sm uppercase tracking-widest font-light transition-colors ${isDark ? 'text-neutral-500 hover:text-white' : 'text-neutral-600 hover:text-black'}`} data-id="element-32">
                  Staff Only
                </button>
              </motion.div>}

            {/* Theme Toggle - Bottom Right with Icons */}
            <motion.div variants={itemVariants} custom={6} className="absolute bottom-8 right-8" data-id="element-33">
              <div className="flex items-center gap-2" data-id="element-34">
                <button onClick={toggleTheme} className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 ${isDark ? 'bg-black/50 hover:bg-black/70' : 'bg-neutral-200 hover:bg-neutral-300'}`} aria-label="Toggle theme" data-id="element-35">
                  <Sun className={`w-4 h-4 transition-all duration-300 ${!isDark ? 'text-black scale-100' : 'text-neutral-600 scale-75'}`} data-id="element-36" />
                  <Moon className={`w-4 h-4 transition-all duration-300 ${isDark ? 'text-white scale-100' : 'text-neutral-400 scale-75'}`} data-id="element-37" />
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}